/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderFormSchemaFactory,
  BuilderUserContext,
  FormRootSchema,
  FormStateSchema,
  whenHasSystemRole,
  whenOfficeUser,
  whenOfficeView,
  whenSwitchedUser,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION,
  PRIVILEGED_EP_USER_UPDATE,
  PRIVILEGED_EP_USER_VERIFICATION,
  SystemRoleBit,
  UserAllowAccess,
  UserStatus,
  UserSystemRole,
} from '@gh/builder-shared-data';
import {
  FormState,
  isRouteForCreationMode,
  MaskedTypes,
  TextTypes,
  toAppFormGroup,
  TYPING_DELAY,
  whenAllOf,
  whenCreateMode,
  whenCreateOrEditMode,
  whenEditMode,
  whenOneOf,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { basketToFormErrors, MessageBasket } from '@gh/core-messages';
import {
  FormMode,
  getControl,
  getControlValue,
  markControlAsDirty,
  markControlAsTouched,
  setControlValue,
  validateControlValue,
} from '@gh/core-ui';
import { coalesce, nullate } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { catchError$, delay$, map$, mapTo$, switchMap$, tap$ } from '@gh/rx/operators';

import { constant, isNil, mapValues } from 'lodash';
import { Observable } from 'rxjs';

import { UserDetailsBusinessService } from '../../resources/user-details-business.service';
import {
  getNormalizedEmailName,
  UserDetails,
  UserDetailsContact,
  UserIntranetCredentials,
  UserValidateLogin,
} from '../../resources/user-details.model';

@Injectable()
export class UserDetailsFormState extends FormState<UserDetails> {
  private originalSendEmailsFrom?: string;

  constructor(private route: ActivatedRoute,
              schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext,
              private businessService: UserDetailsBusinessService) {
    super(schemaFactory);
  }

  get allowAccessToLeads(): UserAllowAccess {
    return getControlValue(this.group, 'allowAccessToLeads');
  }

  get allowAccessToMultipleOffices(): boolean {
    return getControlValue(this.group, 'allowAccessToMultipleOffices');
  }

  get isSalesPerson(): boolean {
    return getControlValue(this.group, 'systemRoles')[UserSystemRole.Salesperson];
  }

  get isIntranetVerified(): boolean {
    return getControlValue(this.group, 'isIntranetVerified');
  }

  get userDataToTransfer(): UserAllowAccess {
    return getControlValue(this.group, 'userDataToTransfer');
  }

  get existingUser(): boolean {
    return getControlValue(this.group, 'existingUser');
  }

  get submitValue(): UserDetails {
    const allowAccessToMultipleOffices = getControlValue(this.group, 'allowAccessToMultipleOffices');
    return {
      ...this.group.value,
      systemRoles: getControlValue(this.group, 'systemRoles'),
      status: getControlValue(this.group, 'status'),
      userEmail: getControlValue(this.group, 'userEmail'),
      allowAccessToLeads: getControlValue(this.group, 'allowAccessToLeads'),
      leadsOf: getControlValue(this.group, 'leadsOf'),
      allowAccessToMultipleOffices,
      // when allowAccessToMultipleOffices is reset => always send original sendEmailsFrom
      sendEmailsFrom: allowAccessToMultipleOffices ?
        getControlValue(this.group, 'sendEmailsFrom')
        : this.originalSendEmailsFrom,
    };
  }

  build(user: UserDetails): void {
    this.originalSendEmailsFrom = user.sendEmailsFrom;

    this.initSchema(user);

    if (user.status === UserStatus.Transferred) {
      getControl(this.group, 'inboxTo').enable();
    }
  }

  setContact(contact: UserDetailsContact): void {
    if (this.createMode) {
      setControlValue(this.group, 'contact', contact);
      setControlValue(this.group, 'userEmail', contact ? getNormalizedEmailName(contact) : '');
      validateControlValue(this.group, 'userEmail');
      markControlAsDirty(this.group, 'userEmail');
      markControlAsTouched(this.group, 'userEmail');
    } else {
      const oldContact = getControlValue(this.group, 'contact');
      setControlValue(this.group, 'contact', contact);
      if (isNil(oldContact) || oldContact.id !== contact.id) {
        markControlAsDirty(this.group, 'contact');
      }
    }
  }

  reassignSales(person: UserDetails): void {
    setControlValue(this.group, 'userToReassignLeadsTo', person);
  }

  getContactId(): UserDetailsContact {
    return getControlValue(this.group, 'contact');
  }

  checkLogin(value: string): Observable<Nullable<ValidationErrors>> {
    if (this.viewMode) {
      return of$(nullate(void 0));
    }
    const existingUser = getControlValue(this.group, 'existingUser');
    if(existingUser) {
      return of$(nullate(void 0));
    }

    return <Observable<Nullable<ValidationErrors>>>this.businessService.validateLogin(
      value,
      getControlValue(this.group, 'userId'),
    ).pipe(
      tap$((response: UserValidateLogin) => {
        const password = getControl(this.group, 'password');

        if (this.createMode) {
          if (response.transferred) {
            password.reset();
            password.disable();
          } else {
            password.enable();
          }
        }
      }),
      mapTo$(void 0),
      map$(nullate),
      catchError$((basket: MessageBasket) => of$(basketToFormErrors(basket))));
  }

  updateIntranetInfo(credentials: UserIntranetCredentials, uid: ID): void {
    setControlValue(this.group, 'intranetGuid', uid);
    markControlAsDirty(this.group, 'intranetGuid');
    setControlValue(this.group, 'intranetUser', credentials.intranetUser);
    markControlAsDirty(this.group, 'intranetUser');
    setControlValue(this.group, 'isIntranetVerified', true);
  }

  updateAllowAccessToLeads(): void {
    const leadsOfControl = toAppFormGroup(getControl(this.group, 'leadsOf'));
    const allowAccessSalespersonLeads = this.allowAccessToLeads === UserAllowAccess.SalespersonLeads;
    leadsOfControl.updateSchema({ visible: allowAccessSalespersonLeads });
  }

  resetAllowAccessToLeads(): void {
    if (!this.isSalesPerson && this.allowAccessToLeads !== UserAllowAccess.OwnLeads) {
      setControlValue(this.group, 'allowAccessToLeads', UserAllowAccess.OwnLeads);
    }
  }

  changeSystemRole(): void {
    const salesPerson: boolean = this.isSalesPerson;
    const statusActive = getControlValue(this.group, 'status') === UserStatus.Active;
    const allowAccessToLeads = getControl(this.group, 'allowAccessToLeads');

    if (salesPerson && statusActive) {
      allowAccessToLeads.enable();
    }

    if (!salesPerson || !statusActive) {
      allowAccessToLeads.reset();
      allowAccessToLeads.disable();
    }

    this.updateAllowAccessToLeads();
    this.resetAllowAccessToLeads();
  }

  existingUserCheck(isExistingUserChecked: boolean): void {
    const fieldsToToggle = ['contact', 'password'];
  
    fieldsToToggle.forEach(field => {
      const control = getControl(this.group, field);
      isExistingUserChecked ? control.disable() : control.enable();
    });
  }  
  
  disableStatusList(): void {
    const status = getControl(this.group, 'status');
    status.disable();
  }
  disableUserDataToTransfer(): void {
    const userDataToTransfer = getControl(this.group, 'userDataToTransfer');
    userDataToTransfer.disable();
    userDataToTransfer.reset();
  }

  enableUserDataToTransfer(): void {
    const userDataToTransfer = getControl(this.group, 'userDataToTransfer');
    userDataToTransfer.enable();
  }

  changeStatus(control: any): void {
    if ([UserStatus.Inactive, UserStatus.Transferred, UserStatus.Deleted].includes(control.value)) {
      this.setReassignToSalesFieldEnabled();
    } else {
      this.setReassignToSalesFieldDisabled();
    }
    const inboxTo = getControl(this.group, 'inboxTo');
    const systemRoles = getControl(this.group, 'systemRoles');
    const allowAccessToLeads = getControl(this.group, 'allowAccessToLeads');
    const leadsOf = getControl(this.group, 'leadsOf');

    inboxTo.disable();
    systemRoles.enable();

    if (systemRoles.value[UserSystemRole.Salesperson]) {
      allowAccessToLeads.enable();
      leadsOf.enable();
    }
    if (control.value === UserStatus.Transferred || control.value === UserStatus.Inactive || control.value === UserStatus.Deleted) {
      inboxTo.enable();
      allowAccessToLeads.disable();
      systemRoles.disable();
      leadsOf.disable();
    }
    if (control.value === UserStatus.Active) {
      inboxTo.reset();
    }
  }

  protected setReassignToSalesFieldEnabled(): void {
    const userToReassignLeadsTo = getControl(this.group, 'userToReassignLeadsTo');
    userToReassignLeadsTo.enable();
  }

  protected setReassignToSalesFieldDisabled(): void {
    const userToReassignLeadsTo = getControl(this.group, 'userToReassignLeadsTo');
    userToReassignLeadsTo.disable();
  }

  protected buildSchema(user: UserDetails, { root, group }: FormStateSchema): FormRootSchema {
    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode
      : (!this.userContext.hasPermission(BuilderPermission.Setup.Manager.UserDetails.EDIT)
      || (user.status === UserStatus.Deleted || user.status === UserStatus.Transferred)
        ? FormMode.ViewMode : FormMode.EditMode);

    const isActive = user.status === UserStatus.Active;

    return root({
      mode,
      optimisticLock: true,
      enabled: whenCreateOrEditMode(),
      privilegedEntryPoints: [
        PRIVILEGED_EP_USER_UPDATE,
        PRIVILEGED_EP_USER_VERIFICATION,
        PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION,
      ],
      confirmations: {
        skipChangeStatusCheck: false,
      },
      children: {
        userId: void 0,
        status: { enabled: whenEditMode() },
        contact: { required: true },
        userEmail: {
          required: true,
          type: TextTypes.GoogleUserName,
          asyncValidators: [(control: AbstractControl) => of$(true).pipe(
            delay$(TYPING_DELAY),
            switchMap$(() => this.checkLogin(control.value)))],
          privileged: PRIVILEGED_EP_USER_UPDATE,
          whenPrivileged: 'none',
        },
        inboxTo: {
          enabled: whenAllOf(
            whenEditMode(),
            constant(user.status === UserStatus.Inactive)),
        },
        systemRoles: group({
          enabled: [whenOfficeView(), isActive],
          children: mapValues(user.systemRoles, constant(void 0)),
        }),
        sendEmailsFrom: {
          visible: whenAllOf(
            whenOneOf(whenSwitchedUser(), whenHasSystemRole(SystemRoleBit.Franchisee)),
            whenCreateOrEditMode(),
            user.allowAccessToMultipleOffices),
          type: TextTypes.GoogleUserName,
          required: true,
          privileged: PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION,
        },
        allowAccessToMultipleOffices: {
          visible: user.usersFromOtherOfficesAccess,
          enabled: whenOneOf(whenSwitchedUser(), whenHasSystemRole(SystemRoleBit.Franchisee)),
          required: true,
        },
        publishToWeb: { visible: whenOneOf(whenOfficeUser(), whenSwitchedUser()) },
        usersFromOtherOfficesAccess: void 0,
        password: { required: true, type: MaskedTypes.GoogleUserPassword, enabled: whenCreateMode() },
        roleId: { required: whenOfficeView() },
        position: { required: whenOfficeView() },
        allowAccessToLeads: {
          visible: whenOfficeView(),
          enabled: [coalesce(user.systemRoles[UserSystemRole.Salesperson], true), isActive],
        },
        leadsOf: group({
          enabled: isActive,
          visible: user.allowAccessToLeads === UserAllowAccess.SalespersonLeads,
          children: mapValues(user.leadsOf, constant(void 0)),
        }),
        userToReassignLeadsTo: {
          visible: coalesce(user.systemRoles[UserSystemRole.Salesperson], false),
          enabled: whenAllOf(coalesce(user.systemRoles[UserSystemRole.Salesperson], false),
                             (user.status === UserStatus.Transferred || user.status === UserStatus.Inactive)),
          required: true,
        },
        userDataToTransfer: {
          enabled: false,
        },
        intranetGuid: void 0,
        intranetUser: void 0,
        isIntranetVerified: void 0,
        existingUser: { enabled: true }
      },
    });
  }
}
